import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutDashboard, List, X, Plus, MapIcon, Palette, Trash2, Pencil, Copy, Check, Filter, MoreVertical, Layers, Calendar } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { blueprintService, Blueprint } from '@/lib/blueprintService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface DesignStudioProps {
  isGlobalDarkMode?: boolean;
  isMobile?: boolean;
}

export const DesignStudio: React.FC<DesignStudioProps> = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'a-z' | 'z-a'>('recent');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Actions states
  const [isCreating, setIsCreating] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Blueprint | null>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const loadItems = useCallback(async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      const loadedBlueprints = await blueprintService.getBlueprintsByWorkspace(currentWorkspace.id);
      setBlueprints(loadedBlueprints);
    } catch (err) {
      console.error('Failed to load blueprints:', err);
      toast.error('Failed to load blueprints');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Filter + sort
  const displayedItems = blueprints
    .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      if (sortBy === 'a-z') return a.name.localeCompare(b.name);
      if (sortBy === 'z-a') return b.name.localeCompare(a.name);
      return 0;
    });

  const handleCreateBlueprint = async () => {
    if (!currentWorkspace?.id || !user?.id) return;
    setIsCreating(true);
    try {
      const blueprint = await blueprintService.createBlueprint(currentWorkspace.id, user.id, 'Untitled Blueprint');
      navigate(`/brand/blueprints/${blueprint.id}?new=true`);
    } catch (err) {
      console.error('Failed to create blueprint:', err);
      toast.error('Failed to create blueprint');
    } finally {
      setIsCreating(false);
    }
  };

  const handleItemClick = (item: Blueprint) => {
    if (renamingId === item.id) return;
    navigate(`/brand/blueprints/${item.id}`);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await blueprintService.deleteBlueprint(itemToDelete.id);
      setBlueprints(prev => prev.filter(i => i.id !== itemToDelete.id));
      toast.success(`Blueprint deleted`);
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDuplicate = async (item: Blueprint) => {
    try {
      await blueprintService.duplicateBlueprint(item.id);
      toast.success(`Duplicated "${item.name}"`);
      loadItems();
    } catch (error: any) {
      console.error('Error duplicating:', error);
      toast.error(error.message || 'Failed to duplicate');
    }
  };

  const startRename = (item: Blueprint) => {
    setRenamingId(item.id);
    setRenameValue(item.name);
  };

  const commitRename = async (item: Blueprint) => {
    if (!renameValue.trim()) return;
    try {
      await blueprintService.renameBlueprint(item.id, renameValue.trim());
      setBlueprints(prev => prev.map(i => i.id === item.id ? { ...i, name: renameValue.trim() } : i));
    } catch (err) {
      console.error('Failed to rename:', err);
      toast.error('Failed to rename');
    } finally {
      setRenamingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const sortOptions = [
    { value: 'recent' as const, label: 'Most Recent' },
    { value: 'a-z' as const, label: 'A → Z' },
    { value: 'z-a' as const, label: 'Z → A' },
  ];

  // Render a grid card
  const renderGridCard = (item: Blueprint) => {
    const sitemapPages = item.sitemap?.pages || [];
    const homepage = sitemapPages.find((p: any) => p.type === 'home');
    const thumbnailUrl = item.sitemap?.thumbnail_url || homepage?.meta?.featuredImage || null;
    const colors = item.designSystem?.color_preset?.colors || [];

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="group relative rounded-2xl overflow-hidden cursor-pointer bg-card border border-border shadow-sm hover:shadow-md transition-all flex flex-col"
        onClick={() => handleItemClick(item)}
      >
        {/* Preview Area - Split left (sitemap image) and right (color palette) */}
        <div className="w-full h-32 flex bg-muted">
          <div className="w-1/2 h-full border-r border-border/50 bg-background overflow-hidden relative flex items-center justify-center">
             {thumbnailUrl ? (
               <img src={thumbnailUrl} alt="Sitemap Preview" className="w-full h-full object-cover" />
             ) : (
               <MapIcon className="w-8 h-8 text-muted-foreground/30" />
             )}
          </div>
          <div className="w-1/2 h-full flex flex-col">
            {colors.length > 0 ? (
               <div className="w-full h-full flex flex-wrap">
                 {colors.slice(0, 4).map((color: any, i: number) => (
                   <div key={i} className="w-1/2 h-1/2" style={{ backgroundColor: color.hex || '#6366F1' }} />
                 ))}
               </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted/50">
                <Palette className="w-8 h-8 text-muted-foreground/30" />
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 flex flex-col gap-2">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {renamingId === item.id ? (
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitRename(item); if (e.key === 'Escape') setRenamingId(null); }}
                    className="flex-1 text-sm font-medium rounded px-2 py-1 border bg-background border-border text-foreground focus:outline-none focus:border-workspace-accent w-full"
                  />
                  <button onClick={() => commitRename(item)} className="p-1 rounded text-green-500 hover:bg-muted"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setRenamingId(null)} className="p-1 rounded text-muted-foreground hover:bg-muted"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <h3 className="font-semibold text-base text-foreground truncate">{item.name}</h3>
              )}
            </div>
            
            {/* Kebab menu */}
            <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1.5 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-0 focus-visible:ring-0 outline-none">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => startRename(item)}>
                    <Pencil className="h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDuplicate(item)}>
                    <Copy className="h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    destructive
                    onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Metadata */}
          <div className="flex flex-col mt-1">
            <div className="flex items-center justify-between pt-3 border-t border-border/50">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(item.updated_at)}
              </span>
              <span className="text-xs text-muted-foreground">
                {sitemapPages.length} pages · {colors.length} colors
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render a list row
  const renderListRow = (item: Blueprint) => {
    const sitemapPages = item.sitemap?.pages || [];
    const homepage = sitemapPages.find((p: any) => p.type === 'home');
    const thumbnailUrl = item.sitemap?.thumbnail_url || homepage?.meta?.featuredImage || null;

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, x: -5 }}
        animate={{ opacity: 1, x: 0 }}
        className="group flex items-center gap-4 p-4 rounded-2xl cursor-pointer bg-card border border-border shadow-sm hover:shadow-md transition-all"
        onClick={() => handleItemClick(item)}
      >
        <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-muted">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <Layers className="w-6 h-6 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {renamingId === item.id ? (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <input
                autoFocus
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(item); if (e.key === 'Escape') setRenamingId(null); }}
                className="flex-1 text-sm font-medium rounded px-2 py-1 border bg-background border-border text-foreground focus:outline-none"
              />
              <button onClick={() => commitRename(item)} className="p-1 rounded text-green-500"><Check className="w-4 h-4" /></button>
              <button onClick={() => setRenamingId(null)} className="p-1 rounded text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <p className="text-sm font-semibold truncate text-foreground">{item.name}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground">
              Updated {formatDate(item.updated_at)}
            </span>
          </div>
        </div>

        <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted focus:ring-0 focus-visible:ring-0 outline-none">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => startRename(item)}>
                <Pencil className="h-4 w-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicate(item)}>
                <Copy className="h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                destructive
                onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return null;
  }

  return (
    <div className="w-full px-6 md:px-10 py-6">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Blueprints</h1>
          <p className="text-muted-foreground mt-1">
            Manage your project Blueprints (Sitemap & Design System)
          </p>
        </div>

        <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'hidden' : ''}`}>
          {/* Search */}
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="search-open"
                  className="relative flex items-center h-10"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search blueprints..."
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* View Toggle */}
          <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-10">
            <motion.div
              className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
              initial={false}
              animate={{
                left: viewMode === 'grid' ? '4px' : 'calc(50%)',
                width: 'calc(50% - 4px)',
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            />
            <button
              onClick={() => setViewMode('grid')}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <LayoutDashboard className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Sort Dropdown */}
          <div className="relative h-10">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className={`p-2.5 h-full rounded-lg border transition-colors ${sortBy !== 'recent'
                ? 'bg-background shadow-sm text-foreground border-transparent'
                : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
              title="Sort"
            >
              <Filter className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[160px] p-1.5 z-40"
                  >
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setSortBy(opt.value); setShowSortMenu(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${sortBy === opt.value
                          ? 'bg-background shadow-sm text-foreground'
                          : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <span>{opt.label}</span>
                        {sortBy === opt.value && <div className="w-2 h-2 bg-current rounded-full" />}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreateBlueprint}
            disabled={isCreating}
            className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap disabled:opacity-60"
          >
            {isCreating ? <LoadingSpinner className="text-white" /> : <Plus className="w-5 h-5" />}
            <span>New Blueprint</span>
          </button>
        </div>
      </div>

      {/* Grid/List View */}
      {displayedItems.length === 0 ? (
        <div className="py-16">
          <div className="p-8 rounded-lg bg-board-column border-none shadow-sm flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 rounded-full mb-4 flex items-center justify-center bg-muted">
              <Layers className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-xl font-semibold mb-2 text-foreground">
              {searchQuery ? 'No results found' : 'Welcome to Blueprints'}
            </p>
            <p className="text-base mb-6 text-center max-w-md text-muted-foreground">
              {searchQuery
                ? 'Try adjusting your search criteria.'
                : 'Create your first blueprint to start mapping and designing.'}
            </p>
            {!searchQuery && (
              <button
                onClick={handleCreateBlueprint}
                disabled={isCreating}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors bg-workspace-accent hover:bg-workspace-accent-hover text-white shadow-sm disabled:opacity-60"
              >
                {isCreating ? <LoadingSpinner className="text-white" /> : <Plus className="w-5 h-5" />}
                <span className="font-medium">New Blueprint</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <section className={viewMode === 'grid' ? 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3' : 'space-y-3'}>
          {displayedItems.map(item =>
            viewMode === 'grid' ? renderGridCard(item) : renderListRow(item)
          )}
        </section>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blueprint</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{itemToDelete?.name}" along with its sitemap and design system records. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export { DesignStudio as Blueprints };
export default DesignStudio;
